import { Download, Loader2, RefreshCw, Share, SquarePlus, WifiOff, X } from 'lucide-react';
import { useState } from 'react';
import {
  applyPwaUpdate,
  dismissPwaUpdate,
  promptPwaInstall,
  usePwaStore,
} from './lifecycle.js';

export function InstallAppButton() {
  const installPrompt = usePwaStore((state) => state.installPrompt);
  const standalone = usePwaStore((state) => state.standalone);
  const ios = usePwaStore((state) => state.ios);
  const online = usePwaStore((state) => state.online);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  if (standalone || (!installPrompt && !ios)) return null;

  const install = () => {
    if (installPrompt) void promptPwaInstall().catch(() => undefined);
    else setIosHelpOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={install}
        disabled={!online}
        className="rounded-full p-2 text-ink-muted hover:bg-hover hover:text-ink disabled:opacity-40"
        title="Install Wintermute on this device"
        aria-label="Install Wintermute on this device"
      >
        <Download className="size-5" />
      </button>
      {iosHelpOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/35 p-3 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ios-install-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIosHelpOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-2xl border border-line bg-surface p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-accent-soft p-2 text-accent">
                <Download className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 id="ios-install-title" className="text-lg font-semibold">
                  Install Wintermute on iPhone or iPad
                </h2>
                <p className="mt-1 text-sm text-ink-muted">
                  Apple installs web apps from Safari rather than showing an automatic prompt.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIosHelpOpen(false)}
                className="rounded-full p-1.5 text-ink-muted hover:bg-hover"
                aria-label="Close installation instructions"
              >
                <X className="size-5" />
              </button>
            </div>
            <ol className="mt-5 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-inset font-semibold">1</span>
                Open this Wintermute address in Safari.
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-inset font-semibold">2</span>
                Tap <Share className="size-4 text-accent" /> <strong>Share</strong> in Safari.
              </li>
              <li className="flex items-center gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-inset font-semibold">3</span>
                Choose <SquarePlus className="size-4 text-accent" /> <strong>Add to Home Screen</strong>.
              </li>
            </ol>
            <p className="mt-5 rounded-xl bg-inset p-3 text-xs text-ink-muted">
              Installation adds the app shell and icon. Private mail remains online-only unless
              you explicitly create a passphrase-encrypted device copy in Settings → Offline.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function PwaStatusBanners() {
  const online = usePwaStore((state) => state.online);
  const waitingWorker = usePwaStore((state) => state.waitingWorker);
  const updateDismissed = usePwaStore((state) => state.updateDismissed);
  const applyingUpdate = usePwaStore((state) => state.applyingUpdate);

  return (
    <>
      {!online ? (
        <div className="flex shrink-0 items-center justify-center gap-2 border-b border-amber-300 bg-amber-50 px-3 py-2 text-center text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100" role="status">
          <WifiOff className="size-4 shrink-0" />
          <span>
            Offline — current content may be stale.{' '}
            <a href="/offline" className="font-semibold underline underline-offset-2">
              Open encrypted offline mail
            </a>{' '}
            if it is enabled on this device.
          </span>
        </div>
      ) : null}
      {waitingWorker && !updateDismissed ? (
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b border-accent/25 bg-accent-soft px-3 py-2 text-xs" role="status">
          <RefreshCw className={`size-4 shrink-0 text-accent ${applyingUpdate ? 'animate-spin' : ''}`} />
          <span>A new Wintermute version is ready. Finish any active edit, then reload.</span>
          <button
            type="button"
            onClick={applyPwaUpdate}
            disabled={applyingUpdate}
            className="rounded-full bg-accent px-3 py-1.5 font-semibold text-white hover:bg-accent-strong disabled:opacity-60"
          >
            {applyingUpdate ? (
              <span className="flex items-center gap-1.5"><Loader2 className="size-3.5 animate-spin" /> Updating</span>
            ) : (
              'Reload now'
            )}
          </button>
          {!applyingUpdate ? (
            <button
              type="button"
              onClick={dismissPwaUpdate}
              className="rounded-full px-2 py-1.5 font-medium text-ink-muted hover:bg-hover hover:text-ink"
            >
              Later
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
